import { Bell, BellOff, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { usePushSubscription } from "../hooks/usePushSubscription";

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString("es-AR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const shortAgent = (ua: string | null): string => {
  if (!ua) return "Dispositivo desconocido";
  const browser = /Edg\//i.test(ua)
    ? "Edge"
    : /OPR\//i.test(ua)
      ? "Opera"
      : /Firefox\//i.test(ua)
        ? "Firefox"
        : /Chrome\//i.test(ua)
          ? "Chrome"
          : /Safari\//i.test(ua)
            ? "Safari"
            : "Browser";
  const platform = /Android/i.test(ua)
    ? "Android"
    : /iPhone|iPad|iOS/i.test(ua)
      ? "iOS"
      : /Mac OS X/i.test(ua)
        ? "macOS"
        : /Windows/i.test(ua)
          ? "Windows"
          : /Linux/i.test(ua)
            ? "Linux"
            : "?";
  return `${browser} on ${platform}`;
};

export const NotificationSettings = () => {
  const {
    isSupported,
    isBackendEnabled,
    permission,
    isSubscribed,
    devices,
    loading,
    error,
    enable,
    disable,
    sendTest,
    removeDevice,
  } = usePushSubscription();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="size-4" /> Notificaciones push
        </CardTitle>
        <CardDescription>
          Recibí alertas críticas (spike de registros, errores) en este
          navegador. Para recibirlas en el cell, instalá el backoffice como app
          desde el menú del navegador → "Add to Home Screen".
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {!isSupported ? (
          <div className="rounded border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-100">
            Tu navegador no soporta web push. Probá Chrome, Edge o Firefox.
          </div>
        ) : null}

        {isSupported && !isBackendEnabled ? (
          <div className="rounded border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-100">
            El backend no tiene VAPID keys configuradas todavía. Las
            notificaciones no van a llegar hasta que se setee{" "}
            <code>VAPID_PUBLIC_KEY</code> y <code>VAPID_PRIVATE_KEY</code>.
          </div>
        ) : null}

        {permission === "denied" ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-100">
            Bloqueaste las notificaciones para este sitio. Activalas desde la
            configuración del navegador y volvé a esta pantalla.
          </div>
        ) : null}

        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-100">
            {error}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          {isSubscribed ? (
            <>
              <Button variant="secondary" onClick={disable} disabled={loading}>
                <BellOff className="mr-2 size-4" />
                Desactivar en este navegador
              </Button>
              <Button variant="outline" onClick={sendTest} disabled={loading}>
                Mandar prueba
              </Button>
            </>
          ) : (
            <Button
              onClick={enable}
              disabled={loading || !isSupported || permission === "denied"}
            >
              <Bell className="mr-2 size-4" />
              Activar notificaciones aquí
            </Button>
          )}
        </div>

        {devices.length > 0 ? (
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Dispositivos suscriptos
            </div>
            <ul className="space-y-2">
              {devices.map((device) => (
                <li
                  key={device.id}
                  className="flex items-center justify-between rounded border border-border/40 p-3 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <Smartphone className="size-4 text-muted-foreground" />
                    <div>
                      <div>{shortAgent(device.userAgent)}</div>
                      <div className="text-xs text-muted-foreground">
                        Último uso: {formatDate(device.lastUsedAt)} · Suscripto:{" "}
                        {formatDate(device.createdAt)}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeDevice(device.id)}
                    disabled={loading}
                    title="Eliminar este dispositivo"
                  >
                    <X className="size-4" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
